import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPhone, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { Helmet, HelmetProvider } from "react-helmet-async";
import axios from "axios";

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
  });

  const [status, setStatus] = useState<"idle" | "success" | "error" | "loading">("idle");

  const validateEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateMobile = (mobile: string): boolean =>
    /^\+?\d{10,15}$/.test(mobile);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { name, email, mobile, message } = formData;

    if (!name || !email || !mobile || !message) {
      alert("All fields are required.");
      return;
    }

    if (!validateEmail(email)) {
      alert("Please enter a valid email address.");
      return;
    }

    if (!validateMobile(mobile)) {
      alert("Please enter a valid mobile number.");
      return;
    }

    if (message.length < 10) {
      alert("Message should be at least 10 characters long.");
      return;
    }

    try {
      setStatus("loading");
      await axios.post("/api/common/sendEmail", {
        name,
        email,
        mobile,
        message,
        source: "Tadwiro Contact Form",
      });
      setStatus("success");
      setFormData({ name: "", email: "", mobile: "", message: "" });
    } catch (error) {
      setStatus("error");
      console.error("Error sending email:", error);
    }
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen bg-gray-100 p-8 flex flex-col items-center">
        <Helmet>
          <title>Contact - Tadwiro by Ayser World</title>
          <meta name="description" content="Contact Ayser World, founder of Tadwiro, for demos or partnerships." />
          <meta name="keywords" content="Tadwiro, contact, Ayser World, software architect" />
        </Helmet>

        <h1 className="text-4xl font-bold text-gray-800 mb-2">Contact Us</h1>
        <p className="text-lg text-gray-600 mb-8 text-center max-w-xl">
          Let's connect about Tadwiro! Whether you're seeking a demo, a solution, or collaboration — I'm listening.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 w-full max-w-6xl">
          {/* Contact Info */}
          <div className="bg-white shadow rounded p-6 space-y-4">
            <h2 className="text-2xl font-semibold text-gray-700">Contact Info</h2>
            <div className="flex items-center gap-4">
              <FontAwesomeIcon icon={faEnvelope} className="text-blue-600 text-xl" />
              <span className="text-gray-800">firojalam.khan@gmail.com</span>
            </div>
            <div className="flex items-center gap-4">
              <FontAwesomeIcon icon={faPhone} className="text-green-600 text-xl" />
              <span className="text-gray-800">+91-9731984079</span>
            </div>
            <div className="flex items-center gap-4">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-600 text-xl" />
              <span className="text-gray-800">Bangalore, India</span>
            </div>
            <div className="pt-6 text-sm text-gray-500">
              Visit my portfolio at{" "}
              <a
                href="https://firojalamkhan.com"
                className="text-blue-500 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                firojalamkhan.com
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white shadow rounded p-6 space-y-6">
            <h2 className="text-2xl font-semibold text-gray-700">Send a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm mb-1 text-gray-700">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
                />
              </div>

              <div>
                <label className="block text-sm mb-1 text-gray-700">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
                />
              </div>

              <div>
                <label className="block text-sm mb-1 text-gray-700">Mobile Number</label>
                <input
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  required
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
                  placeholder="+91-xxxxxxxxxx"
                />
              </div>

              <div>
                <label className="block text-sm mb-1 text-gray-700">Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={5}
                  required
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
                />
              </div>

              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition"
                disabled={status === "loading"}
              >
                {status === "loading" ? "Sending..." : "Send Message"}
              </button>

              {status === "success" && (
                <p className="text-green-600 text-sm pt-2">Your message has been sent successfully!</p>
              )}
              {status === "error" && (
                <p className="text-red-600 text-sm pt-2">Failed to send message. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </HelmetProvider>
  );
};

export default Contact;